import { useMsal } from '@azure/msal-react'
import { faExpand } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Button,
  Container,
  Grid,
  Group,
  Image,
  LoadingOverlay,
  Modal,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { AdStatusModal } from '../components/AdStatusModal'
import { AdGrid } from '../components/device/AdGrid'
import { DeviceInfoCard } from '../components/device/DeviceInfoCard'
import { ExpandedAdModal } from '../components/device/ExpandedAdModal'
import { UploadAdModal } from '../components/location/UploadAdModal'
import {
  fetchAllAdsByDeviceId,
  updateDeviceAdvertisementStatus,
} from '../data/advertisement.service'
import {
  deleteDevice,
  fetchDeviceById,
  updateDeviceVisibility,
} from '../data/device.service'
import { fetchLocationById } from '../data/location.service'
import { Device } from '../models/Device'
import { DeviceAdvertisements } from '../models/DeviceAdvertisements'
import { Location } from '../models/Location'
import { RunningAdvertisementDto } from '../models/RunningAdvertisementDto'
import { AdStatuses, COLORS, HostOnboardingSteps } from '../utils/constants'

export function DevicePage() {
  const { deviceId } = useParams()
  const { instance } = useMsal()
  const navigate = useNavigate()
  const [device, setDevice] = useState<Device | null>(null)
  const [location, setLocation] = useState<Location | null>(null)
  const [runningAds, setRunningAds] = useState<RunningAdvertisementDto[]>([])
  const [upcomingAds, setUpcomingAds] = useState<RunningAdvertisementDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [uploadAdModalOpen, setUploadAdModalOpen] = useState(false)
  const [expandedAd, setExpandedAd] = useState<RunningAdvertisementDto | null>(
    null,
  )
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [selectedAd, setSelectedAd] = useState<RunningAdvertisementDto | null>(
    null,
  )
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false)

  useEffect(() => {
    const fetchData = async () => {
      if (!deviceId) return

      try {
        // First fetch device data
        const deviceData = await fetchDeviceById(deviceId)
        setDevice(deviceData)

        // Fetch location data to get organizationId
        const locationData = await fetchLocationById(
          deviceData.locationId.toString(),
        )
        setLocation(locationData)

        // Then fetch ads data using the organizationId from location
        const adsData = await fetchAllAdsByDeviceId(
          parseInt(deviceId),
          instance,
        )

        // Separate running and upcoming ads (no need to filter by device name since it's done on server)
        const running = adsData
          .filter((ad) => ad.status === AdStatuses.Running)
          .map((ad) => ({
            id: ad.id,
            imageUrl: ad.adImageUrl,
            startDate: ad.startDateLocal
              ? new Date(ad.startDateLocal)
              : new Date(),
            endDate: ad.endDateLocal ? new Date(ad.endDateLocal) : new Date(),
            status: ad.status,
            organizationId: ad.organizationId,
          }))
        const upcoming = adsData
          .filter((ad) => ad.status === AdStatuses.Upcoming)
          .map((ad) => ({
            id: ad.id,
            imageUrl: ad.adImageUrl,
            startDate: ad.startDateLocal
              ? new Date(ad.startDateLocal)
              : new Date(),
            endDate: ad.endDateLocal ? new Date(ad.endDateLocal) : new Date(),
            status: ad.status,
            organizationId: ad.organizationId,
          }))
        setRunningAds(running)
        setUpcomingAds(upcoming)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchData()
  }, [deviceId, instance])

  const handleDeleteDevice = async () => {
    if (!device) return

    try {
      setIsDeleting(true)
      await deleteDevice(device, instance)

      notifications.show({
        title: 'Device Deleted',
        message: 'The device has been successfully deleted.',
        color: 'green',
      })

      navigate(`/locations/${device.locationId}`)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete the device.',
        color: 'red',
      })
    } finally {
      setIsDeleting(false)
      setDeleteModalOpen(false)
    }
  }

  const enableDevice = async () => {
    if (!device) return

    const notificationId = notifications.show({
      title: 'Updating Device',
      message: 'Making device visible...',
      loading: true,
      autoClose: false,
    })

    try {
      const updatedDevice = { ...device, isVisible: true }
      await updateDeviceVisibility(updatedDevice, instance)
      setDevice(updatedDevice)

      notifications.update({
        id: notificationId,
        title: 'Device Shown',
        message: 'The device has been successfully made visible.',
        color: 'green',
        loading: false,
        autoClose: true,
      })
    } catch (error) {
      notifications.update({
        id: notificationId,
        title: 'Error',
        message: 'Cannot change device visibility.',
        color: 'red',
        loading: false,
        autoClose: true,
      })
    }
  }

  const disableDevice = async () => {
    if (!device) return

    const notificationId = notifications.show({
      title: 'Updating Device',
      message: 'Hiding device...',
      loading: true,
      autoClose: false,
    })

    try {
      const updatedDevice = { ...device, isVisible: false }
      await updateDeviceVisibility(updatedDevice, instance)
      setDevice(updatedDevice)

      notifications.update({
        id: notificationId,
        title: 'Device Hidden',
        message: 'The device has been successfully hidden.',
        color: 'green',
        loading: false,
        autoClose: true,
      })
    } catch (error) {
      notifications.update({
        id: notificationId,
        title: 'Error',
        message: 'Cannot change device visibility.',
        color: 'red',
        loading: false,
        autoClose: true,
      })
    }
  }

  const handleAdUploaded = (newAdvertisement: DeviceAdvertisements) => {
    // Add the new advertisement to the appropriate list based on status
    const newAd: RunningAdvertisementDto = {
      id: newAdvertisement.id!,
      imageUrl: newAdvertisement.advertisement.imageUrl,
      startDate: newAdvertisement.startDateLocal,
      endDate: newAdvertisement.endDateLocal,
      status: newAdvertisement.status,
      organizationId: newAdvertisement.organizationId,
    }

    if (newAdvertisement.status === AdStatuses.Running) {
      setRunningAds((prevAds) => [...prevAds, newAd])
    } else if (newAdvertisement.status === AdStatuses.Upcoming) {
      setUpcomingAds((prevAds) => [...prevAds, newAd])
    }
  }

  const handleDisableAd = async () => {
    if (!selectedAd) return

    setIsUpdatingStatus(true)
    const notificationId = notifications.show({
      title: 'Updating Advertisement',
      message: 'Disabling advertisement...',
      loading: true,
      autoClose: false,
    })

    try {
      await updateDeviceAdvertisementStatus(selectedAd.id, instance, false)
      setRunningAds((prevAds) =>
        prevAds.filter((ad) => ad.id !== selectedAd.id),
      )
      setUpcomingAds((prevAds) =>
        prevAds.filter((ad) => ad.id !== selectedAd.id),
      )

      notifications.update({
        id: notificationId,
        title: 'Success',
        message: 'Advertisement has been disabled',
        color: 'green',
        loading: false,
        autoClose: true,
      })
      setShowStatusModal(false)
    } catch (error) {
      notifications.update({
        id: notificationId,
        title: 'Error',
        message: 'Failed to disable advertisement',
        color: 'red',
        loading: false,
        autoClose: true,
      })
    } finally {
      setIsUpdatingStatus(false)
      setSelectedAd(null)
    }
  }

  if (isLoading) {
    return (
      <Container size='xl' py='xl' w={'100%'}>
        <Stack gap='xl'>
          {/* Device Info Skeleton */}
          <Paper shadow='md' radius='lg' p='xl' withBorder>
            <Grid gutter='xl' align='center'>
              <Grid.Col span={{ base: 12, md: 5 }}>
                <AspectRatio ratio={16 / 9}>
                  <Skeleton height='100%' radius='md' />
                </AspectRatio>
              </Grid.Col>
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap='md'>
                  <Group justify='space-between' align='center'>
                    <Stack gap='xs'>
                      <Skeleton height={40} width={300} radius='md' />
                      <Skeleton height={24} width={150} radius='md' />
                    </Stack>
                  </Group>

                  <Group gap='xl'>
                    <Stack gap='xs'>
                      <Skeleton height={24} width={120} radius='md' />
                      <Skeleton height={32} width={180} radius='md' />
                    </Stack>
                  </Group>
                </Stack>
              </Grid.Col>
            </Grid>
          </Paper>

          {/* Ads Grid Skeleton */}
          <Stack gap='md'>
            <Group justify='space-between' align='center'>
              <Skeleton height={40} width={300} radius='md' />
              <Skeleton height={24} width={200} radius='md' />
            </Group>
            <Grid>
              {[1, 2, 3, 4].map((index) => (
                <Grid.Col key={index} span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper shadow='sm' radius='md' withBorder>
                    <AspectRatio ratio={16 / 9}>
                      <Skeleton height='100%' radius='md' />
                    </AspectRatio>
                    <Stack p='md' gap='xs'>
                      <Skeleton height={20} width={200} radius='md' />
                    </Stack>
                  </Paper>
                </Grid.Col>
              ))}
            </Grid>
          </Stack>
        </Stack>
      </Container>
    )
  }

  if (!device || !location) {
    return (
      <Container size='xl' py='xl'>
        <Text c='red'>Device not found</Text>
      </Container>
    )
  }

  return (
    <Container size='xl' py='xl'>
      <Stack gap='xl'>
        <DeviceInfoCard
          device={device}
          location={location}
          onManageDevice={() =>
            navigate(`/devices/edit/${device.id}`, {
              state: {
                location,
                device: device,
                isHostOnboarding: false,
                currentStep: HostOnboardingSteps.DevicePhotos,
              },
            })
          }
          onToggleVisibility={device.isVisible ? disableDevice : enableDevice}
          onDeleteDevice={() => setDeleteModalOpen(true)}
        />

        <AdGrid
          runningAds={runningAds}
          onUploadClick={() => setUploadAdModalOpen(true)}
          onExpandAd={setExpandedAd}
          onDisableAd={(ad) => {
            setSelectedAd(ad)
            setShowStatusModal(true)
          }}
        />

        {/* Upcoming Ads Section */}
        <Stack gap='md'>
          <Group justify='space-between' align='center'>
            <Title order={2}>Upcoming Advertisements</Title>
            <Text size='sm' c='dimmed'>
              {upcomingAds.length} upcoming advertisements
            </Text>
          </Group>
          {upcomingAds.length === 0 ? (
            <Paper shadow='sm' radius='md' p='xl' withBorder>
              <Stack align='center' gap='md'>
                <Text size='lg' c='dimmed'>
                  No Upcoming Advertisements
                </Text>
                <Text size='sm' c='dimmed'>
                  This device currently has no upcoming advertisements.
                </Text>
              </Stack>
            </Paper>
          ) : (
            <Grid>
              {upcomingAds.map((ad) => (
                <Grid.Col key={ad.id} span={{ base: 12, sm: 6, md: 3 }}>
                  <Paper shadow='sm' radius='md' withBorder>
                    <div style={{ position: 'relative' }}>
                      <AspectRatio ratio={16 / 9}>
                        <Image
                          src={ad.imageUrl}
                          alt={`Advertisement ${ad.id}`}
                          radius='md'
                        />
                      </AspectRatio>
                      <ActionIcon
                        variant='filled'
                        radius='md'
                        onClick={() => setExpandedAd(ad)}
                        style={{
                          position: 'absolute',
                          bottom: 8,
                          right: 8,
                          backgroundColor: 'rgba(0, 0, 0, 0.5)',
                          width: 32,
                          height: 32,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        visibleFrom='sm'
                      >
                        <FontAwesomeIcon
                          icon={faExpand}
                          style={{ fontSize: '14px', color: 'white' }}
                        />
                      </ActionIcon>
                    </div>
                    <Stack p='md' gap='xs'>
                      <Group justify='space-between' align='center'>
                        <Text size='xs' c='dimmed'>
                          {ad.startDate && ad.endDate
                            ? `${new Date(
                                ad.startDate,
                              )?.toLocaleDateString()} - ${new Date(
                                ad.endDate,
                              )?.toLocaleDateString()}`
                            : 'No dates specified'}
                        </Text>
                        <Text
                          component='button'
                          onClick={() => {
                            setSelectedAd(ad)
                            setShowStatusModal(true)
                          }}
                          c={COLORS.HiddenGemzRed}
                          style={{
                            textDecoration: 'underline',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                        >
                          Disable Ad
                        </Text>
                      </Group>
                    </Stack>
                  </Paper>
                </Grid.Col>
              ))}
            </Grid>
          )}
        </Stack>
      </Stack>

      {/* Modals */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title='Delete Device'
        centered
        size='sm'
        radius='lg'
      >
        <Stack pos='relative'>
          {isDeleting && <LoadingOverlay />}
          <Text>
            Are you sure you want to delete this device? This action cannot be
            undone.
          </Text>
          <Group justify='flex-end' mt='md'>
            <Button
              variant='light'
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color='red'
              onClick={handleDeleteDevice}
              loading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      {uploadAdModalOpen && (
        <UploadAdModal
          opened={uploadAdModalOpen}
          onClose={() => setUploadAdModalOpen(false)}
          deviceId={device?.id!}
          deviceName={device?.name!}
          organizationId={location?.organizationId!}
          onAdUploaded={handleAdUploaded}
        />
      )}

      <ExpandedAdModal ad={expandedAd} onClose={() => setExpandedAd(null)} />

      <AdStatusModal
        ad={
          selectedAd
            ? {
                id: selectedAd.id,
                status: AdStatuses.Running,
                deviceName: device?.name || '',
                organizationName: '',
                hostEmail: '',
                advertiserEmail: '',
                adImageUrl: selectedAd.imageUrl,
                startDateLocal: selectedAd.startDate
                  ? new Date(selectedAd.startDate).toISOString()
                  : '',
                endDateLocal: selectedAd.endDate
                  ? new Date(selectedAd.endDate).toISOString()
                  : '',
                locationId: device?.locationId || 0,
                locationName: location?.name || '',
                organizationId: selectedAd?.organizationId || 0,
                autoRenew: false,
                autoRenewCancelled: false,
                autoRenewCancelledDate: null,
                numberOfDaysToRunAd: 0,
                denialReason: undefined,
                advertisementId: 0,
                priceSnapshot: 0,
              }
            : null
        }
        isOpen={showStatusModal}
        onClose={() => {
          setShowStatusModal(false)
          setSelectedAd(null)
        }}
        onConfirm={handleDisableAd}
        isUpdating={isUpdatingStatus}
      />
    </Container>
  )
}
